import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';
import {
  ShieldExclamationIcon, CheckCircleIcon, WifiIcon, TrashIcon, PlusIcon,
} from '@heroicons/react/24/outline';

/**
 * What the till scanner noticed.
 *
 * Deliberately a page rather than a notification badge: these are judgements
 * about people, and the right response to one is to look at the evidence
 * beside it, not to act on a number in a corner.
 *
 * Nothing here blocks anything. A scan is never refused and nothing shows at
 * the till -- somebody who knows they are watched changes method, and a false
 * accusation at a counter in front of a customer costs more than a missed
 * discount.
 */
const LEVELS = {
  high:   { label: 'Serious', color: '#B91C1C', bg: '#FEF2F2' },
  medium: { label: 'Worth a look', color: '#B45309', bg: '#FFFBEB' },
  low:    { label: 'Housekeeping', color: '#475569', bg: '#F8FAFC' },
};

export default function ScanAlertsPage() {
  const [alerts, setAlerts]   = useState([]);
  const [nets, setNets]       = useState([]);
  const [myIp, setMyIp]       = useState('');
  const [loading, setLoading] = useState(true);
  const [openOnly, setOpenOnly] = useState(true);
  const [label, setLabel]     = useState('');
  const [ip, setIp]           = useState('');
  const [err, setErr]         = useState('');
  const [busy, setBusy]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, n, m] = await Promise.all([
        adminAPI.scanAlerts({ open: openOnly ? 1 : undefined, limit: 200 }),
        adminAPI.storeNetworks(),
        adminAPI.myIp().catch(() => ({ ip: '' })),
      ]);
      setAlerts(a.alerts || []);
      setNets(n.networks || []);
      setMyIp(m.ip || '');
    } catch (e) {
      setErr(e.message || 'Could not load.');
    } finally {
      setLoading(false);
    }
  }, [openOnly]);

  useEffect(() => { load(); }, [load]);

  const addNetwork = async () => {
    if (!label.trim() || !ip.trim()) return;
    setBusy(true); setErr('');
    try {
      await adminAPI.addStoreNetwork({ label: label.trim(), ip: ip.trim() });
      setLabel(''); setIp('');
      await load();
    } catch (e) {
      setErr(e.message || 'Could not add it.');
    } finally { setBusy(false); }
  };

  const ack = async (id) => {
    setBusy(true);
    try {
      await adminAPI.ackScanAlert(id, {});
      await load();
    } catch (e) {
      setErr(e.message || 'Could not acknowledge it.');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setBusy(true);
    try { await adminAPI.deleteStoreNetwork(id); await load(); }
    catch (e) { setErr(e.message || 'Could not remove it.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
        <ShieldExclamationIcon style={{ width:26, height:26, color:'#B91C1C' }} />
        <h1 style={{ margin:0, fontSize:24 }}>Scan alerts</h1>
      </div>
      <p style={{ color:'var(--text-muted)', marginTop:0, maxWidth:720 }}>
        Memberships that may have been used on somebody else&rsquo;s basket. Nothing
        is ever blocked and nothing shows at the till &mdash; these are for you to
        look at, not for the shop floor.
      </p>

      {err && (
        <div style={{ background:'#FEF2F2', color:'#B91C1C', padding:'10px 14px',
                      borderRadius:8, marginBottom:16 }}>{err}</div>
      )}

      {/* ── The shop networks ───────────────────────────────────────────── */}
      <section style={{ background:'#fff', borderRadius:12, padding:20, marginBottom:24,
                        border:'1px solid var(--border)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
          <WifiIcon style={{ width:18, height:18, color:'#0891B2' }} />
          <h2 style={{ margin:0, fontSize:16 }}>Shop networks</h2>
        </div>
        <p style={{ color:'var(--text-muted)', fontSize:13, marginTop:0, maxWidth:720 }}>
          The public addresses the shops sit behind. With no guest wifi, a customer at
          the counter is always on mobile data &mdash; so a membership shown from a phone
          on one of these addresses belongs to somebody who works here. Until at least
          one is added, that check stays off.
        </p>

        {myIp && (
          <p style={{ fontSize:13, background:'#F0F9FF', padding:'8px 12px',
                      borderRadius:8, display:'inline-block' }}>
            You are reading this from <strong style={{ fontFamily:'monospace' }}>{myIp}</strong>.
            {' '}Open this page on the till to read that shop&rsquo;s address.
            <button
              className="btn-ghost"
              style={{ marginLeft:8, fontSize:12 }}
              onClick={() => setIp(myIp)}
            >Use it</button>
          </p>
        )}

        <div style={{ display:'flex', gap:8, flexWrap:'wrap', margin:'10px 0 14px' }}>
          <input
            placeholder="Which shop (e.g. Ramlet El Bayda)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            style={{ flex:'2 1 220px', padding:'9px 12px', border:'1px solid var(--border)',
                     borderRadius:8, fontSize:14 }}
          />
          <input
            placeholder="Public IP address"
            value={ip}
            onChange={e => setIp(e.target.value)}
            style={{ flex:'1 1 180px', padding:'9px 12px', border:'1px solid var(--border)',
                     borderRadius:8, fontSize:14, fontFamily:'monospace' }}
          />
          <button className="btn-primary" disabled={busy} onClick={addNetwork}>
            <PlusIcon style={{ width:15, height:15 }} /> Add
          </button>
        </div>

        {nets.length === 0 ? (
          <p style={{ color:'var(--text-subtle)', fontSize:13, margin:0 }}>
            None yet. Ask IT for the public address of each shop, or open this page on
            each till and use the address shown above.
          </p>
        ) : (
          <table style={{ width:'100%', fontSize:14 }}>
            <tbody>
              {nets.map(n => (
                <tr key={n.id}>
                  <td style={{ padding:'6px 0', fontWeight:600 }}>{n.label}</td>
                  <td style={{ fontFamily:'monospace' }}>{n.ip}</td>
                  <td style={{ textAlign:'right' }}>
                    <button className="btn-ghost" disabled={busy}
                            onClick={() => remove(n.id)} title="Remove">
                      <TrashIcon style={{ width:15, height:15 }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── The alerts ──────────────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
        <label style={{ fontSize:14, display:'flex', alignItems:'center', gap:6 }}>
          <input type="checkbox" checked={openOnly}
                 onChange={e => setOpenOnly(e.target.checked)} />
          Only ones nobody has looked at
        </label>
        <button className="btn-ghost" onClick={load} disabled={loading}>Refresh</button>
      </div>

      {loading ? (
        <p style={{ color:'var(--text-muted)' }}>Loading…</p>
      ) : alerts.length === 0 ? (
        <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:12,
                      padding:40, textAlign:'center' }}>
          <CheckCircleIcon style={{ width:32, height:32, color:'#059669' }} />
          <h3 style={{ margin:'10px 0 4px' }}>Nothing flagged</h3>
          <p style={{ color:'var(--text-muted)', margin:0, fontSize:14 }}>
            Scans are being recorded. Anything worth a second look will appear here.
          </p>
        </div>
      ) : alerts.map(a => {
        const lv = LEVELS[a.level] || LEVELS.low;
        return (
          <div key={a.id} style={{
            background:'#fff', border:'1px solid var(--border)', borderLeft:`4px solid ${lv.color}`,
            borderRadius:10, padding:'14px 18px', marginBottom:10,
            opacity: a.acknowledged_at ? 0.55 : 1,
          }}>
            <div style={{ display:'flex', justifyContent:'space-between', gap:12 }}>
              <div style={{ minWidth:0 }}>
                <span style={{ background:lv.bg, color:lv.color, fontSize:11, fontWeight:700,
                               padding:'2px 8px', borderRadius:20 }}>{lv.label}</span>
                <div style={{ fontWeight:600, marginTop:6 }}>{a.title}</div>
                <div style={{ color:'var(--text-muted)', fontSize:13, marginTop:3 }}>{a.detail}</div>
                <div style={{ color:'var(--text-subtle)', fontSize:12, marginTop:6 }}>
                  {a.member_name && <>{a.member_name} · </>}
                  {a.card_no && <span style={{ fontFamily:'monospace' }}>{a.card_no}</span>}
                  {a.staff_name ? <> · till: {a.staff_name}</> : <> · nobody signed in</>}
                  {a.ip && <> · <span style={{ fontFamily:'monospace' }}>{a.ip}</span></>}
                  {' · '}{format(new Date(a.created_at), 'dd MMM HH:mm')}
                </div>
              </div>
              {!a.acknowledged_at && (
                <button className="btn-ghost" disabled={busy} onClick={() => ack(a.id)}
                        style={{ whiteSpace:'nowrap', alignSelf:'flex-start' }}>
                  Looked at it
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
